import {
  ButtonClickTrackingPlugin,
  enableButtonClickTracking,
} from "@snowplow/browser-plugin-button-click-tracking";
import {
  ErrorTrackingPlugin,
  enableErrorTracking,
} from "@snowplow/browser-plugin-error-tracking";
import {
  LinkClickTrackingPlugin,
  enableLinkClickTracking,
} from "@snowplow/browser-plugin-link-click-tracking";
import {
  addGlobalContexts,
  enableActivityTracking,
  newTracker,
  setUserId,
  trackPageView,
  trackSelfDescribingEvent,
} from "@snowplow/browser-tracker";

import { DEFAULT_TRACKER_CONFIG } from "@config/tracking-config";
import { Impersonator, Organization } from "@models/tracking/entities";
import { EVENT_TYPES } from "@models/tracking/event-types";

/**
 * SnowplowTrackingService is a utility class that integrates Snowplow tracking into the application.
 * It manages tracker initialization, global contexts, and event tracking for custom use cases.
 */
class SnowplowTrackingService {
  #mainTrackerId = "";
  #organizationCode = "";
  #userId = "";
  #impersonatorId = "";
  #initialized = false;

  /**
   *
   * @param {string} appId - The application identifier to associate with the events being tracked.
   * @param {boolean} enabled - Indicates whether the tracker is enabled. If `false`, no events will be sent.
   */
  constructor(appId, enabled = false) {
    this.appId = appId;
    this.enabled = enabled;
  }

  /**
   * Sets the organization code, which is used to generate tracking contexts.
   * @param {string} organizationCode - The unique identifier for the organization.
   */
  setOrganizationCode(organizationCode) {
    this.#organizationCode = organizationCode;
  }

  /**
   * Sets the userId to be attached to all events.
   * @param {string} userId - The unique identifier for the current user.
   */
  setUserId(userId) {
    this.#userId = userId;
  }

  setImpersonatorId(impersonatorId) {
    this.#impersonatorId = impersonatorId;
  }

  /**
   * Checks if the Snowplow tracker has been properly initialized and is ready to track events.
   * @returns {boolean} True if tracker is initialized and ready, false otherwise
   */
  isInitialized() {
    return this.#initialized;
  }

  /**
   * Generates global contexts for events based on event type and current service state.
   * Excludes organization context for page views and link clicks.
   * Adds impersonator context when an impersonator ID is present.
   * @private
   * @param {object} args - Event arguments containing eventType
   * @returns {Array} Array of context entities for the event
   */
  #contextGenerator = (args) => {
    const excludedTypes = [EVENT_TYPES.PAGE_VIEW, EVENT_TYPES.LINK_CLICK];

    if (excludedTypes.includes(args.eventType)) {
      return [];
    }

    const contexts = [];

    if (this.#organizationCode) {
      contexts.push(new Organization(this.#organizationCode).toContext());
    }

    if (this.#impersonatorId) {
      contexts.push(new Impersonator(this.#impersonatorId).toContext());
    }

    return contexts;
  };

  /**
   * Handles failed requests to the Snowplow collector. This callback is triggered when a request fails.
   *
   * @private
   * @param {Object} failure - The failure information from Snowplow
   */
  #handleRequestFailure(failure) {
    if (!failure.willRetry) {
      console.error("Snowplow events lost after retry attempts:", {
        status: failure.status,
        message: failure.message,
        eventCount: failure.events.length,
        events: failure.events,
      });
    }
  }

  setUser(data) {
    if (this.enabled && this.#initialized) {
      this.setUserId(data?.userId);
      this.setImpersonatorId(data?.impersonatorId);
      this.setOrganizationCode(data?.organizationCode);
      setUserId(this.#userId);
    }
  }

  /**
   * Initializes the Snowplow tracker by configuring it with organization code and user ID.
   * This setup occurs before the tracker is fully initialized and begins tracking events.
   *
   * @param {string} trackerId - The unique identifier for the Snowplow tracker instance.
   * @param {string} collectorUrl - The endpoint URL of the Snowplow collector to receive event data.
   * @param {string} growthbookTrackerId - The unique identifier for the Growthbook tracker instance.
   * @param {string} growthbookCollectorUrl - The endpoint URL of the Snowplow collector to receive Growthbook events.
   * @param {string} orgCode - A unique code for the organization, included with all tracked events.
   * @param {string} userId - A unique identifier for the user, attached to all events to track user actions.
   * @param {Object} [config=DEFAULT_TRACKER_CONFIG] - Optional configuration object to customize the tracker settings. Defaults to `DEFAULT_TRACKER_CONFIG` if not provided.
   */
  configure({ currentUser, ...config }) {
    if (!this.enabled) {
      console.info("Snowplow tracker not enabled");
      return;
    }

    if (currentUser) {
      this.setUser(currentUser);
    }

    this.initializeTracker(config);
  }

  /**
   * Initializes the Snowplow tracker with the given configuration and settings.
   * Sets up plugins, global contexts, activity tracking, and tracks an initial page view.
   * This method also configures cookies, performance timing, and tracking for user activity and button/link clicks.
   *
   * @param {string} trackerId - The unique identifier for the Snowplow tracker instance.
   * @param {string} collectorUrl - The endpoint URL of the Snowplow collector to send event data to.
   * @param {string} growthbookTrackerId - The unique identifier for the Growthbook tracker instance.
   * @param {string} growthbookCollectorUrl - The endpoint URL of the Snowplow collector to receive Growthbook events.
   * @param {Object} [config] - Optional configuration object to customize tracker behavior.
   */
  initializeTracker({
    trackerId,
    collectorUrl,
    growthbookTrackerId,
    growthbookCollectorUrl,
    config: {
      cookieSameSite,
      performanceTiming,
      minimumVisitLength,
      heartbeatDelay,
    } = DEFAULT_TRACKER_CONFIG,
  }) {
    this.#mainTrackerId = trackerId;

    newTracker(trackerId, collectorUrl, {
      appId: this.appId,
      cookieSameSite,
      contexts: {
        performanceTiming,
      },
      plugins: [
        ButtonClickTrackingPlugin(),
        LinkClickTrackingPlugin(),
        ErrorTrackingPlugin(),
      ],
      onRequestFailure: this.#handleRequestFailure,
    });

    newTracker(growthbookTrackerId, growthbookCollectorUrl, {
      appId: this.appId,
      cookieSameSite,
      contexts: {
        performanceTiming,
      },
      onRequestFailure: this.#handleRequestFailure,
    });

    // Add global contexts to both trackers
    addGlobalContexts([this.#contextGenerator]);
    setUserId(this.#userId);

    enableErrorTracking(undefined, [trackerId]);

    enableActivityTracking(
      {
        minimumVisitLength,
        heartbeatDelay,
      },
      [trackerId]
    );

    enableButtonClickTracking(undefined, [trackerId]);
    enableLinkClickTracking({ pseudoClicks: true }, [trackerId]);

    trackPageView(undefined, [trackerId]);
    this.#initialized = true;
  }

  /**
   * Tracks a custom event using a self-describing event schema.
   * Allows for flexible tracking of domain-specific or custom events.
   * @param {object} event - The event payload, which includes the schema and data.
   * @param {Array} [contexts=[]] - Optional additional contexts to attach to the event.
   * @param {string} [trackerId=undefined] - The unique identifier for the
   * tracker instance to use for this event. If not provided, the main tracker
   * will be used.
   */
  trackEvent(event, contexts = [], trackerId = undefined) {
    if (!this.enabled || !this.#initialized) {
      return;
    }

    trackSelfDescribingEvent(
      {
        event,
        context: contexts,
      },
      [trackerId ?? this.#mainTrackerId]
    );
  }
}

export default SnowplowTrackingService;
